/**
 * Design System - Card Component
 *
 * A flexible card component with header, body, and footer sections.
 *
 * @example
 * <Card>
 *   <Card.Header>
 *     <h3>Card Title</h3>
 *   </Card.Header>
 *   <Card.Body>
 *     Card content goes here
 *   </Card.Body>
 *   <Card.Footer>
 *     <Button>Action</Button>
 *   </Card.Footer>
 * </Card>
 *
 * @example
 * <Card variant="bordered" padding="compact">
 *   Simple card content
 * </Card>
 */

import React from 'react';
import PropTypes from 'prop-types';
import { cn } from '../utils/classNames';

const Card = ({
  children,
  variant = 'elevated',
  padding = 'default',
  className,
  ...props
}) => {
  // Variant styles
  const variantStyles = {
    elevated: 'bg-white rounded-lg shadow-md border border-gray-200',
    bordered: 'bg-white rounded-lg border-2 border-gray-200',
    flat: 'bg-white rounded-lg',
    soft: 'bg-white rounded-lg shadow-soft border border-gray-100',
  };

  // Padding styles
  const paddingStyles = {
    none: '',
    compact: 'p-4',
    default: 'p-6',
    spacious: 'p-8',
  };

  return (
    <div
      className={cn(
        variantStyles[variant],
        paddingStyles[padding],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['elevated', 'bordered', 'flat', 'soft']),
  padding: PropTypes.oneOf(['none', 'compact', 'default', 'spacious']),
  className: PropTypes.string,
};

// Card Header subcomponent
const CardHeader = ({ children, className, ...props }) => (
  <div
    className={cn('border-b border-gray-200 pb-4 mb-4', className)}
    {...props}
  >
    {children}
  </div>
);

CardHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// Card Body subcomponent
const CardBody = ({ children, className, ...props }) => (
  <div className={cn('space-y-4', className)} {...props}>
    {children}
  </div>
);

CardBody.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// Card Footer subcomponent
const CardFooter = ({ children, className, ...props }) => (
  <div
    className={cn('border-t border-gray-200 pt-4 mt-4', className)}
    {...props}
  >
    {children}
  </div>
);

CardFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

// Attach subcomponents to Card
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

export default Card;
